<template>
  <div
    class="szw-card"
    style="font-family: 'Plus Jakarta Sans', Inter, sans-serif;"
    @click="$router.push('/actors/' + actor.id)"
  >
    <!-- Header: avatar + name + badges + transfer button -->
    <div class="tw-flex items-start gap-3">
      <ActorAvatar :actor="actor" :width="40" variant="rounded" prefer="screenshot"/>
      <div class="tw-flex flex-col flex-1 min-w-0 pt-0.5">
        <p class="text-[13px] font-bold text-[#191c1d] m-0 leading-snug line-clamp-1">{{ actor.name }}</p>
        <div v-if="ownerName" class="tw-flex items-center gap-1 flex-wrap mt-1">
          <span class="szw-badge szw-badge--blue">{{ ownerName }}</span>
          <span v-if="legendLabel" class="szw-badge szw-badge--grey">{{ legendLabel }}</span>
        </div>
      </div>
      <div v-if="showTransfer" class="flex-shrink-0" @click.stop>
        <button class="szw-icon-btn" @click="$emit('transfer', $event)">
          <span class="material-symbols-outlined" style="font-size: 16px; color: #454650;">swap_horiz</span>
        </button>
      </div>
    </div>

    <!-- Body: description or contributors -->
    <div class="szw-body">
      <!-- Description -->
      <p v-if="description && !displayContributors.length"
         class="text-[12px] text-[#757681] m-0 line-clamp-3 leading-relaxed">
        {{ stripHtmlTags(description) }}
      </p>

      <!-- Contributors -->
      <template v-else-if="displayContributors.length">
        <div v-for="c in visibleContributors" :key="c.user_id" class="szw-contributor">
          <div class="szw-contributor__avatar">
            <img v-if="c.photo_url" :src="c.photo_url" class="szw-contributor__avatar-img" :alt="c.user_name"/>
            <span v-else class="szw-contributor__avatar-initials">{{ nameInitials(c.user_name) }}</span>
          </div>
          <div class="szw-contributor__text">
            <span class="szw-contributor__name">{{ c.user_name }}</span>
            <a
              v-if="showPhone && contributorPhone(c.user_id)"
              :href="'tel:' + contributorPhone(c.user_id)"
              class="szw-contributor__phone"
              @click.stop
            >
              <span class="material-symbols-outlined" style="font-size: 11px;">phone</span>
              {{ contributorPhone(c.user_id) }}
            </a>
            <span v-else-if="c.function || c.department" class="szw-contributor__role">{{ contributorRole(c) }}</span>
          </div>
        </div>
        <p v-if="extraContributorCount > 0" class="szw-contributor__extra">+{{ extraContributorCount }}</p>
      </template>
    </div>

    <!-- Actor phone/notes (when in network and no contributor phones) -->
    <template v-if="networkContact && !showPhone && !hideContact">
      <div class="szw-contact">
        <a
          v-if="networkContact.phone"
          :href="'tel:' + networkContact.phone"
          class="szw-contact__phone"
          @click.stop
        >
          <span class="material-symbols-outlined" style="font-size: 13px;">phone</span>
          {{ networkContact.phone }}
        </a>
        <p v-if="networkContact.notes" class="szw-contact__notes">{{ networkContact.notes }}</p>
        <template v-for="(entry, ci) in contributorEntries" :key="ci">
          <div v-if="entry.phone || entry.notes" class="szw-contact__entry">
            <a v-if="entry.phone" :href="'tel:' + entry.phone" class="szw-contact__phone" @click.stop>
              <span class="material-symbols-outlined" style="font-size: 12px;">phone</span>
              {{ entry.phone }}
            </a>
            <p v-if="entry.notes" class="szw-contact__notes">{{ entry.notes }}</p>
          </div>
        </template>
      </div>
    </template>

    <!-- Footer: add button (only for non-readOnly) -->
    <div v-if="!readOnly" class="szw-footer" @click.stop>
      <span v-if="!ownerName && legendLabel" class="szw-badge szw-badge--grey">{{ legendLabel }}</span>
      <span v-else class="flex-1"></span>
      <button
        v-if="isInNetwork"
        class="szw-btn szw-btn--in-network"
        disabled
      >
        <span class="material-symbols-outlined" style="font-size: 15px;">check</span>
        {{ $t('samergo_home_in_network') }}
      </button>
      <button
        v-else
        class="szw-btn"
        :class="adding ? 'szw-btn--loading' : 'szw-btn--add'"
        :disabled="adding"
        @click.stop="handleAdd($event)"
      >
        <span class="material-symbols-outlined" style="font-size: 15px;">{{ adding ? 'hourglass_empty' : 'person_add' }}</span>
        {{ adding ? $t('samergo_home_adding') : $t('samergo_home_add') }}
      </button>
    </div>
  </div>

  <!-- Owner picker -->
  <Teleport to="body">
    <div
      v-if="pickerOpen && pickerPos"
      class="szw-popup"
      :style="{ top: pickerPos.top + 'px', right: pickerPos.right + 'px' }"
      @click.stop
    >
      <div class="szw-popup__header">
        <span class="material-symbols-outlined szw-popup__header-icon">hub</span>
        <span class="szw-popup__header-label">{{ $t('samergo_network_add_picker_title') }}</span>
      </div>
      <div class="szw-popup__options">
        <button
          v-for="claim in myClaims"
          :key="claim.actor_id"
          class="szw-popup__option"
          @click="addAs(claim.actor_id)"
        >
          <ActorAvatar :actor="claim" :width="32" variant="rounded"/>
          <span class="szw-popup__option-name">{{ claim.name }}</span>
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { ACTION_TYPES } from '@/store/modules/network-contacts.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'
  import { fetchMyClaims } from '@/api/network-contacts'
  import { fetchContributors } from '@/api/actors'
  import TaxonomyMixin from '@/Domain/Taxonomy/Mixins/TaxonomyMixin'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import { stripHtmlTags } from '@/util/helpers'
  import TranslationsMixin from '@/util/TranslationsMixin'

  export default defineComponent({
    name: 'SamergoActorCard',
    components: { ActorAvatar },
    mixins: [TaxonomyMixin, TranslationsMixin],
    emits: ['transfer'],
    props: {
      actor: { type: Object, required: true },
      contributors: { type: Array, default: () => [] },
      description: { type: String, default: '' },
      readOnly: { type: Boolean, default: false },
      showPhone: { type: Boolean, default: false },
      ownerName: { type: String, default: '' },
      showTransfer: { type: Boolean, default: false },
      hideContact: { type: Boolean, default: false },
      claimedActors: { type: Array, default: null },
    },
    data() {
      return {
        adding: false,
        pickerOpen: false,
        pickerPos: null as { top: number; right: number } | null,
        fetchedClaims: null as Array<{ actor_id: number; elastic_id: string; name: string; logo: string | null }> | null,
        fetchedContributors: null as any[] | null,
        noteEditing: false,
        noteDraft: '',
        noteSaving: false,
      }
    },
    created() {
      if (this.claimedActors === null) this.loadClaims()
      if (this.showPhone) this.loadContributors()
    },
    mounted() {
      document.addEventListener('click', this.closePicker)
    },
    beforeUnmount() {
      document.removeEventListener('click', this.closePicker)
    },
    computed: {
      isInNetwork(): boolean {
        return this.$store.getters.isNetworkContact(this.actor.id)
      },
      networkContact(): any {
        const contacts: any[] = this.$store.getters.networkContacts
        return contacts.find((c: any) => c.elastic_id === this.actor.id) ?? null
      },
      contributorEntries(): Array<{ phone: string; notes: string }> {
        return Object.values(this.networkContact?.contributors_data ?? {}) as Array<{ phone: string; notes: string }>
      },
      displayContributors(): any[] {
        return this.showPhone ? (this.fetchedContributors ?? []) : (this.contributors as any[])
      },
      visibleContributors(): any[] {
        return this.displayContributors.slice(0, 3)
      },
      extraContributorCount(): number {
        return Math.max(0, this.displayContributors.length - 3)
      },
      myClaims(): Array<{ actor_id: number; elastic_id: string; name: string; logo: string | null }> {
        return (this.claimedActors as any[] | null) ?? this.fetchedClaims ?? []
      },
      legendLabel(): string {
        if (!this.actor.legend) return ''
        const id = this.actor.legend
        // Resolve via the ecosystem's configured legend property (identical to the My Network
        // card's getLabelForTaxonomyValue) so the label is translated regardless of which
        // taxonomy backs the legend. Reading `this.locale` keeps this reactive to language switches.
        const industryId = this.actor?.industries?.[0] ?? this.actor?.legend
        if (industryId == null) return ''
        return this.getLabelForTaxonomyValue('industries', industryId, this.locale)
      },
      taxonomyIdToLabel(): Record<string, string> {
        const maps = [
          this.$store.getters.categoryValuesAndLabels,
          this.$store.getters.activityValuesAndLabels,
          this.$store.getters.domainValuesAndLabels,
          this.$store.getters.technologyValuesAndLabels,
          this.$store.getters.membershipValuesAndLabels,
        ]
        const out: Record<string, string> = {}
        maps.forEach((m: Array<{ value: string; label: string }>) => {
          if (Array.isArray(m)) m.forEach(({ value, label }) => {
            out[value] = label
          })
        })
        return out
      },
    },
    methods: {
      stripHtmlTags,
      contributorPhone(userId: number): string {
        return (this.networkContact?.contributors_data ?? {})[userId]?.phone ?? ''
      },
      async loadContributors() {
        try {
          const res = await fetchContributors(this.actor.id, { includeOwners: true })
          this.fetchedContributors = Array.isArray(res) ? res : []
        } catch (e) {
          this.fetchedContributors = []
        }
      },
      contributorRole(c: any): string {
        // Resolve the localized taxonomy label (getTranslatedTaxonomyName reads the term's
        // translations[locale].name with a fallback to the base name).
        const fnTaxonomy = c.function
          ? (this.$store.state.taxonomies.experienceLevels as any[])?.find((e: any) => e.id === Number(c.function))
          : null
        const deptTaxonomy = c.department
          ? (this.$store.state.taxonomies.departments as any[])?.find((d: any) => d.id === Number(c.department))
          : null
        const fn = fnTaxonomy ? this.getTranslatedTaxonomyName(fnTaxonomy) : ''
        const dept = deptTaxonomy ? this.getTranslatedTaxonomyName(deptTaxonomy) : ''
        return [fn, dept].filter(Boolean).join(' · ') || c.function || c.department || ''
      },
      nameInitials(name: string): string {
        if (!name) return '?'
        const parts = name.trim().split(/\s+/)
        return parts.length >= 2
          ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
          : name.slice(0, 2).toUpperCase()
      },
      async loadClaims() {
        try {
          this.fetchedClaims = await fetchMyClaims()
        } catch {
          // non-critical
        }
      },
      handleAdd(event: MouseEvent) {
        if (this.adding || this.isInNetwork) return
        if (!this.myClaims.length) {
          // Users need an active claim before they can add actors to their social map
          this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { actorRequiredVariant: 'network' })
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ACTOR_CLAIM_REQUIRED)
          return
        }
        if (this.myClaims.length === 1) {
          this.addAs(this.myClaims[0].actor_id)
        } else if (this.myClaims.length > 1) {
          if (this.pickerOpen) {
            this.closePicker();
            return
          }
          const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
          this.pickerPos = { top: rect.bottom + 8, right: window.innerWidth - rect.right }
          this.pickerOpen = true
        }
      },
      closePicker() {
        this.pickerOpen = false
        this.pickerPos = null
      },
      startNote() {
        this.noteDraft = this.networkContact?.notes || ''
        this.noteEditing = true
        this.$nextTick(() => (this.$refs.noteInput as HTMLTextAreaElement)?.focus())
      },
      cancelNote() {
        this.noteEditing = false
      },
      async saveNote() {
        if (this.noteSaving) return
        this.noteSaving = true
        try {
          await this.$store.dispatch(ACTION_TYPES.UPDATE_CONTACT, {
            elasticId: this.actor.id,
            phone: this.networkContact?.phone ?? null,
            notes: this.noteDraft,
            contributors_data: this.networkContact?.contributors_data ?? {},
          })
          this.noteEditing = false
        } finally {
          this.noteSaving = false
        }
      },
      async addAs(ownerActorId: number) {
        this.closePicker()
        if (this.adding || this.isInNetwork) return
        this.adding = true
        try {
          await this.$store.dispatch(ACTION_TYPES.ADD_CONTACT, {
            elasticId: this.actor.id,
            name: this.actor.name,
            image: this.actor.website_screenshot || this.actor.logo || '',
            ownerActorId,
          })
        } finally {
          this.adding = false
        }
      },
    },
  })
</script>

<style scoped>
  .szw-card {
    background: #fff;
    border-radius: 16px;
    border: 1px solid #c6c5d2;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
    height: 100%;
  }

  .szw-card:hover {
    border-color: #071c5b;
    box-shadow: 0 4px 16px rgba(7, 28, 91, 0.08);
  }

  /* Badge */
  .szw-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 99px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .szw-badge--blue {
    background: #dde1ff;
    color: #324381;
  }

  .szw-badge--grey {
    background: #f3f4f5;
    color: #454650;
  }

  /* Icon button (transfer) */
  .szw-icon-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: 1px solid #c6c5d2;
    background: #fff;
    cursor: pointer;
    transition: background 0.15s;
    flex-shrink: 0;
  }

  .szw-icon-btn:hover {
    background: #f3f4f5;
  }

  /* Body */
  .szw-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
  }

  /* Personal note (empty-state) */
  .szw-note-cta {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 600;
    color: #071c5b;
    background: transparent;
    border: 1px dashed #c6c5d2;
    border-radius: 8px;
    padding: 6px 10px;
    cursor: pointer;
    width: fit-content;
    transition: background 0.15s, border-color 0.15s;
  }

  .szw-note-cta:hover {
    background: #f3f4f8;
    border-color: #071c5b;
  }

  .szw-note {
    font-size: 12px;
    color: #757681;
    margin: 0;
    cursor: text;
    line-height: 1.4;
    white-space: pre-wrap;
  }

  .szw-note-edit {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .szw-note-edit__input {
    font-size: 12px;
    color: #191c1d;
    border: 1px solid #c6c5d2;
    border-radius: 8px;
    padding: 6px 8px;
    resize: vertical;
    min-height: 52px;
    font-family: inherit;
    outline: none;
  }

  .szw-note-edit__input:focus {
    border-color: #071c5b;
  }

  .szw-note-edit__actions {
    display: flex;
    gap: 6px;
    justify-content: flex-end;
  }

  .szw-note-edit__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid #c6c5d2;
    background: #fff;
    color: #454650;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }

  .szw-note-edit__btn:hover {
    background: #f3f4f5;
  }

  .szw-note-edit__btn--save {
    background: #071c5b;
    color: #fff;
    border-color: #071c5b;
  }

  .szw-note-edit__btn--save:hover {
    background: #0a2a85;
  }

  /* Contributors */
  .szw-contributor {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .szw-contributor__avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    flex-shrink: 0;
    overflow: hidden;
    background: #eef0f8;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .szw-contributor__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .szw-contributor__avatar-initials {
    font-size: 10px;
    font-weight: 700;
    color: #071c5b;
    letter-spacing: 0.03em;
  }

  .szw-contributor__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 1px;
  }

  .szw-contributor__name {
    font-size: 12px;
    font-weight: 600;
    color: #191c1d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .szw-contributor__role {
    font-size: 11px;
    color: #757681;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .szw-contributor__phone {
    display: flex;
    align-items: center;
    gap: 2px;
    font-size: 11px;
    font-weight: 600;
    color: #071c5b;
    text-decoration: none;
    white-space: nowrap;
  }

  .szw-contributor__phone:hover {
    text-decoration: underline;
  }

  .szw-contributor__extra {
    font-size: 11px;
    font-weight: 600;
    color: #757681;
    padding-left: 36px;
    margin: 0;
  }

  /* Contact section */
  .szw-contact {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
  }

  .szw-contact__phone {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 600;
    color: #071c5b;
    text-decoration: none;
    width: fit-content;
  }

  .szw-contact__phone:hover {
    text-decoration: underline;
  }

  .szw-contact__notes {
    font-size: 11px;
    color: #757681;
    margin: 0;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .szw-contact__entry {
    display: flex;
    flex-direction: column;
    gap: 2px;
    background: #f3f4f5;
    border-radius: 8px;
    padding: 6px 8px;
    margin-top: 2px;
  }

  /* Footer */
  .szw-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 4px;
    margin-top: auto;
  }

  /* Buttons */
  .szw-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    padding: 5px 10px;
    border-radius: 8px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    white-space: nowrap;
  }

  .szw-btn--add {
    background: #fff;
    color: #00a03a;
    border-color: #00a03a;
  }

  .szw-btn--add:hover {
    background: #00a03a;
    color: #fff;
  }

  .szw-btn--in-network {
    background: #1f9d43;
    color: #fff;
    border-color: transparent;
    cursor: default;
  }

  .szw-btn--loading {
    background: #f3f4f5;
    color: #aaa;
    border-color: #e0e0e0;
    cursor: wait;
  }
</style>
